import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import PropTypes from "prop-types";
import React, { Fragment, useRef } from "react";

type ConfirmationModalProps = {
  modalIsOpen: boolean;
  closeModal: () => void;
  handleSubmit: (e: any) => void;
  children?: React.ReactNode;
  cancel: () => void;
  isSubmitting?: boolean;
  confirmationText: string;
  buttonText?: string;
  headerText?: string;
  submittedText?: string;
};

function ConfirmationModal({
  modalIsOpen,
  closeModal,
  cancel,
  handleSubmit,
  confirmationText,
  isSubmitting,
  buttonText = "Proceed",
  headerText = "Confirm changes",
  submittedText = "",
}: ConfirmationModalProps) {
  const proceedButtonRef = useRef(null);
  return (
    <Transition.Root show={modalIsOpen} as={Fragment}>
      <Dialog
        as="div"
        static
        initialFocus={proceedButtonRef}
        className="fixed modal inset-0 overflow-y-auto"
        open={modalIsOpen}
        onClose={closeModal}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-passio-font focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={cancel}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <Dialog.Title
                    as="h3"
                    className="mb-4 text-lg leading-6 font-medium text-passio-font"
                  >
                    {headerText}
                  </Dialog.Title>
                  {isSubmitting
                    ? submittedText || confirmationText
                    : confirmationText}
                </div>
              </div>
              <div className="sm:mt-2 sm:flex sm:flex-row-reverse">
                <form>
                  <div className="flex justify-end p-4">
                    <button
                      ref={proceedButtonRef}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit(e);
                      }}
                      disabled={isSubmitting}
                      className="inline-flex items-center px-4 py-1 mr-2 rounded-passio border border-transparent text-sm font-medium rounded shadow-sm text-white bg-passio-purple hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-passio-purple"
                      type="submit"
                    >
                      {isSubmitting ? "Proceeding..." : buttonText}
                    </button>
                    <button
                      className="inline-flex items-center px-4 py-1 rounded-passio border border-transparent text-sm font-medium rounded shadow-sm text-white bg-passio-buttonColor hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-passio-buttonColor mr-2"
                      onClick={(e) => {
                        e.preventDefault();
                        cancel();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

ConfirmationModal.propTypes = {
  modalIsOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  cancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};
ConfirmationModal.defaultProps = {
  children: [],
};

export default ConfirmationModal;
